import { COLLECTIONS_WITH_CHANGE_HISTORY } from "@finway-group/shared/lib/consts"
import { CollectionNameEnum } from "@finway-group/shared/lib/models"
import { checkIsDateWithinRetentionPeriodOfChangeHistory } from "@finway-group/shared/lib/utils/date.utils"
import { Alert, DatePicker, Form, Select, Space, Tooltip } from "antd"
import { FormInstance, FormProps } from "antd/lib/form"
import moment from "moment"
import React, { ReactNode, useRef } from "react"
import { useTranslation } from "react-i18next"
import { ConditionalWrapper } from "~Components/conditionalWrapper"
import { AuthzService } from "~Shared/services"
import { doFilterSelect } from "~Shared/utils/helper.utils"
import { getPopupAnchor } from "~Shared/utils/popup.utils"
import { createChangeHistoryRules } from "./rules/changeHistory.rules"

interface ChangeHistoryFormProps {
    formInstance: FormInstance
    onSubmit: FormProps["onFinish"]
}

export const ChangeHistoryForm = ({ formInstance, onSubmit }: ChangeHistoryFormProps) => {
    const { t } = useTranslation()
    const anchorRef = useRef(null)
    const changeHistoryRules = createChangeHistoryRules()
    const userIsAllowedToExportRoles = AuthzService.isAllowedToExportRoleChangeHistory()

    return (
        <Form id="changeHistoryForm" ref={anchorRef} form={formInstance} onFinish={onSubmit} layout="vertical">
            <Form.Item required label={t("input:export.change_history.resource")} name="resource" rules={changeHistoryRules.resource}>
                <Select showSearch filterOption={doFilterSelect} getPopupContainer={getPopupAnchor()}>
                    {COLLECTIONS_WITH_CHANGE_HISTORY.map((collection) => {
                        const disabled = !userIsAllowedToExportRoles && collection === CollectionNameEnum.ROLE
                        return (
                            <Select.Option key={collection} value={collection} label={t(`label:resources.${collection?.toLowerCase()}`)} disabled={disabled}>
                                <div>
                                    <ConditionalWrapper
                                        condition={disabled}
                                        wrapper={(children: ReactNode) => (
                                            <Tooltip title={t("tooltips:export_change_history.no_roles_access")} placement="topLeft" getPopupContainer={getPopupAnchor()}>
                                                {children}
                                            </Tooltip>
                                        )}
                                    >
                                        <span>{t(`label:resources.${collection?.toLowerCase()}`)}</span>
                                    </ConditionalWrapper>
                                </div>
                            </Select.Option>
                        )
                    })}
                </Select>
            </Form.Item>

            <Space direction="horizontal" className="w-full">
                <Form.Item required label={t("input:export.change_history.period")} name="changeHistoryPeriod" rules={changeHistoryRules.changeHistoryPeriod}>
                    <DatePicker.RangePicker
                        style={{ width: "100%" }}
                        format={moment.localeData().longDateFormat("L")}
                        placeholder={[t("placeholder:start_date"), t("placeholder:end_date")]}
                        getPopupContainer={getPopupAnchor()}
                        disabledDate={(current) => current && !checkIsDateWithinRetentionPeriodOfChangeHistory(current)}
                    />
                </Form.Item>
            </Space>
            <Alert
                message={<span className="font-bold">{t("info:export_change_history.message")}</span>}
                description={t("info:export_change_history.description")}
                type="info"
                showIcon
            />
        </Form>
    )
}
